import React from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import get from "lodash.get";
import {formatRupees} from "../../utils/pcNonPayable";

/**
 * The runtime construct behind the tariff - `data.dynamic_construct` on the tariff response,
 * read only, in four sections that follow the order the engine works in:
 *
 *   Drivers          what the discharge summary gave it, and what each one matched to. The
 *                    ones it could not place are here too, marked as unmatched.
 *   Item inclusions  the items those drivers allow, and how many of each.
 *   Category         the same for whole categories, for the items no master knows by name.
 *   Clusters         what the bill actually charged, grouped by likeness, against those caps.
 *
 * Above the four, four cards: the length of stay allowed and the driver that set it, how many
 * inputs were placed, what the construct takes off the bill, and how many lines it flagged.
 * Every "allowed total" of 14 below is that one LOS figure, so the cards are what make the
 * tables readable.
 *
 * A construct is a code everywhere in this payload - `sources` on an inclusion, `construct_code`
 * on a driver - and a code is not a thing a person reads. `drivers.matched` is the one place
 * both the code and its name appear, so it is read once into a lookup and every code on screen
 * is shown by name.
 */

const codeToName = (construct) => {
    const named = {};
    (get(construct, ['drivers', 'matched'], []) || []).forEach((driver) => {
        const code = get(driver, ['construct_code']);
        if (code && !named[code]) {
            named[code] = get(driver, ['name'], code);
        }
    });
    return named;
};

const DASH = '—';

const sectionStyle = {
    border: '1px solid #e3e9f1',
    borderRadius: '6px',
    marginBottom: '8px',
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: '0 0 8px 0'},
};

// The triangle points at the section when it is shut and down into it when it is open, so the
// default 180deg flip is replaced by a quarter turn.
const summarySx = {
    background: '#f4f7fb',
    borderRadius: '6px',
    minHeight: '38px',
    '&.Mui-expanded': {minHeight: '38px', borderBottom: '1px solid #e3e9f1', borderRadius: '6px 6px 0 0'},
    '& .MuiAccordionSummary-content': {margin: '8px 0'},
    '& .MuiAccordionSummary-content.Mui-expanded': {margin: '8px 0'},
    '& .MuiAccordionSummary-expandIconWrapper': {transform: 'rotate(0deg)', color: '#41597a'},
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {transform: 'rotate(90deg)'},
};

const sectionTitleStyle = {fontSize: '0.8rem', fontWeight: 700, color: '#22405f'};
const sectionCountStyle = {fontSize: '0.75rem', color: '#8195ad', paddingRight: '4px'};

const headerCellStyle = {
    fontSize: '0.6rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#41597a',
    background: '#eef2f8',
    borderBottom: '1px solid #dde5ef',
    padding: '5px 10px',
    whiteSpace: 'nowrap',
};

const cellStyle = {
    fontSize: '0.75rem',
    color: '#2f4560',
    padding: '6px 10px',
    borderBottom: '1px solid #eef2f7',
    verticalAlign: 'top',
};

const numberCellStyle = {...cellStyle, textAlign: 'right', whiteSpace: 'nowrap'};
const strongNumberStyle = {...numberCellStyle, fontWeight: 700, color: '#22405f'};
const codeStyle = {fontFamily: 'monospace', fontSize: '0.73rem', color: '#22405f'};
const linkishStyle = {color: '#1c5fa8'};
const rowSx = {'&:nth-of-type(even)': {background: '#f8fafd'}};

const chipStyle = {
    display: 'inline-block',
    fontSize: '0.68rem',
    lineHeight: 1.5,
    padding: '0 7px',
    margin: '2px 4px 2px 0',
    borderRadius: '999px',
    border: '1px solid #cfe0f5',
    background: '#eef4fd',
    color: '#1c5fa8',
    whiteSpace: 'nowrap',
};

const typeChipStyle = {
    ...chipStyle,
    fontWeight: 700,
    letterSpacing: '0.04em',
    border: '1px solid #cbd8e8',
    background: '#eaf0f8',
    color: '#41597a',
};

const noMatchChipStyle = {
    ...chipStyle,
    fontWeight: 700,
    letterSpacing: '0.03em',
    border: '1px solid #f3c9c9',
    background: '#fdeeee',
    color: '#c0392b',
    whiteSpace: 'normal',
};

// PAYABLE, CAPPED and DISALLOWED are the three answers a cluster can come back with; anything
// else the engine adds later still gets a chip rather than bare text.
const STATUS_COLOURS = {
    PAYABLE: {border: '#bfe3c8', background: '#e9f7ed', color: '#22803f'},
    CAPPED: {border: '#f0dcae', background: '#fdf4e0', color: '#9a6b12'},
    DISALLOWED: {border: '#f3c9c9', background: '#fdeeee', color: '#c0392b'},
    REVIEW: {border: '#cbd8e8', background: '#eaf0f8', color: '#41597a'},
};

const statusChipStyle = (status) => ({
    ...chipStyle,
    fontWeight: 700,
    letterSpacing: '0.03em',
    ...(STATUS_COLOURS[status] || STATUS_COLOURS.REVIEW),
});

const appliedChipStyle = (applied) => ({
    ...chipStyle,
    fontWeight: 700,
    letterSpacing: '0.03em',
    margin: 0,
    ...(applied ? STATUS_COLOURS.PAYABLE : STATUS_COLOURS.DISALLOWED),
});

/**
 * The same illness reaches the engine from more than one input - "Acute Enterocolitis" and
 * "Enterocolitis" both land on ILL248 - so the basis comes back with repeats. They are one
 * fact, not two, and the longest stay among them is the one that became `allowed_los`, so it
 * leads and is the only one drawn as the figure the tables below are built on.
 */
const losBasis = (construct) => {
    const seen = {};
    const out = [];
    (get(construct, ['los_basis'], []) || []).forEach((basis) => {
        const key = `${get(basis, ['construct_code'], '')}|${get(basis, ['los_allowed'], '')}`;
        if (!seen[key]) {
            seen[key] = true;
            out.push(basis);
        }
    });
    return out.sort((a, b) => (get(b, ['los_allowed'], 0) || 0) - (get(a, ['los_allowed'], 0) || 0));
};

/**
 * The band above the four sections, as four cards: the length of stay the construct allowed,
 * how many of the discharge summary's inputs it could place, what it takes off the bill, and
 * how many bill lines it did not wave through.
 *
 * Each card is a figure and the working behind it, because none of the four reads on its own -
 * "14 days" only means something next to the driver that set it, and "3 flagged" only next to
 * the split between disallowed, capped and under review.
 */
const cardRowStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '10px',
};

const summaryCardStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: '1 1 180px',
    minWidth: '170px',
    padding: '9px 13px',
    borderRadius: '8px',
    background: '#ffffff',
    border: '1px solid #e3e9f1',
    boxShadow: '0 1px 2px rgba(18, 49, 91, 0.05)',
};

const cardLabelStyle = {
    fontSize: '0.6rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#8195ad',
};

const cardValueStyle = {
    fontSize: '1.3rem',
    fontWeight: 700,
    lineHeight: 1.2,
    fontVariantNumeric: 'tabular-nums',
};

const cardDetailStyle = {fontSize: '0.68rem', lineHeight: 1.45, color: '#63788f'};

// The construct not having run at all is not a figure, so it is not a card - it is the notice
// that says nothing below it was taken off this claim.
const notAppliedStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '7px 11px',
    marginBottom: '10px',
    borderRadius: '8px',
    border: '1px solid #f3c9c9',
    background: '#fdeeee',
};

function SummaryCard({label, value, colour, detail, tooltip}) {
    const card = (
        <Box style={summaryCardStyle}>
            <Typography style={cardLabelStyle}>{label}</Typography>
            <Typography style={{...cardValueStyle, color: colour}}>{value}</Typography>
            {detail && <Typography style={cardDetailStyle}>{detail}</Typography>}
        </Box>
    );
    return tooltip ? <Tooltip title={tooltip}>{card}</Tooltip> : card;
}

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * What the length of stay was read off. One driver holding the figure on its own needs no list -
 * naming it says everything the figure did not. More than one and the figure is the highest of
 * them, so all of them are named with their own stay beside it.
 */
const basisDetail = (basis) => {
    if (basis.length === 0) {
        return 'no driver set a length of stay';
    }
    if (basis.length === 1) {
        return `off ${get(basis, [0, 'name'], get(basis, [0, 'construct_code'], DASH))}`;
    }
    const listed = basis
        .map((entry) => `${get(entry, ['name'], get(entry, ['construct_code'], DASH))}`
            + ` (${get(entry, ['los_allowed'], DASH)})`)
        .join(', ');
    return `max across drivers: ${listed}`;
};

const DEDUCTION_TOOLTIP = 'The units the construct did not allow, priced at what the bill charged'
    + ' for them. An estimate, and not the claim\'s own saving - that one is on the band above,'
    + ' and it moves with every decision taken by hand.';

function SummaryCards({construct, deduction}) {
    const applied = get(construct, ['applied'], false);
    const allowedLos = get(construct, ['allowed_los']);
    const basis = losBasis(construct);
    const matched = get(construct, ['drivers', 'matched'], []) || [];
    const unmatched = get(construct, ['drivers', 'unmatched'], []) || [];
    const summary = get(construct, ['summary'], {}) || {};

    const disallowed = get(summary, ['disallowed_lines'], 0) || 0;
    const capped = get(summary, ['capped_lines'], 0) || 0;
    const review = get(summary, ['review_lines'], 0) || 0;
    const lines = get(summary, ['lines'], 0) || 0;
    const payable = get(summary, ['payable_lines'], 0) || 0;

    return (
        <Box>
            {!applied && (
                <Box style={notAppliedStyle}>
                    <span style={appliedChipStyle(false)}>CONSTRUCT NOT APPLIED</span>
                    <Typography style={cardDetailStyle}>
                        Nothing below has been taken off this claim — it is what the construct
                        would have said had it run.
                    </Typography>
                </Box>
            )}

            <Box style={cardRowStyle}>
                <SummaryCard
                    label="Allowed LOS"
                    value={allowedLos || allowedLos === 0 ? `${allowedLos} days` : DASH}
                    colour="#0f8a6a"
                    detail={basisDetail(basis)}
                    tooltip="Every per-day allowance below is this figure multiplied out."
                />
                <SummaryCard
                    label="Drivers matched"
                    value={matched.length}
                    colour="#1c5fa8"
                    detail={unmatched.length > 0
                        ? `${plural(unmatched.length, 'input')} unmatched`
                        : 'every input matched'}
                    tooltip="Illnesses, departments and procedures read off the discharge summary."
                />
                {(deduction || deduction === 0) && (
                    <SummaryCard
                        label="Construct deduction"
                        value={formatRupees(deduction)}
                        colour="#c0392b"
                        detail="estimated from billed amounts"
                        tooltip={DEDUCTION_TOOLTIP}
                    />
                )}
                <SummaryCard
                    label="Lines flagged"
                    value={disallowed + capped + review}
                    colour="#c07a12"
                    detail={`${disallowed} disallowed · ${capped} capped · ${review} review`}
                    tooltip={lines > 0 ? `${payable} of ${lines} bill lines payable` : ''}
                />
            </Box>
        </Box>
    );
}

function Section({title, count, children}) {
    return (
        <Accordion disableGutters sx={sectionStyle}>
            <AccordionSummary expandIcon={<ArrowRightIcon/>} sx={summarySx}>
                <Box style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: '12px',
                }}>
                    <Typography style={sectionTitleStyle}>{title}</Typography>
                    <Typography style={sectionCountStyle}>{count}</Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails style={{padding: 0}}>
                <Box style={{overflowX: 'auto'}}>{children}</Box>
            </AccordionDetails>
        </Accordion>
    );
}

const SourceChips = ({sources, named}) => (
    <Box>
        {(sources || []).map((code) => (
            <span key={code} style={chipStyle}>{named[code] || code}</span>
        ))}
    </Box>
);

function DriversSection({construct}) {
    const matched = get(construct, ['drivers', 'matched'], []) || [];
    const unmatched = get(construct, ['drivers', 'unmatched'], []) || [];

    return (
        <Section title="Drivers (from discharge summary)" count={matched.length + unmatched.length}>
            <Table size="small" style={{minWidth: '640px'}}>
                <TableHead>
                    <TableRow>
                        <TableCell style={headerCellStyle}>Input</TableCell>
                        <TableCell style={headerCellStyle}>Construct</TableCell>
                        <TableCell style={headerCellStyle}>Type</TableCell>
                        <TableCell style={{...headerCellStyle, textAlign: 'right'}}>LOS</TableCell>
                        <TableCell style={headerCellStyle}>Department</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {matched.map((driver, index) => (
                        <TableRow key={`matched-${index}`} sx={rowSx}>
                            <TableCell style={cellStyle}>{get(driver, ['input'], DASH)}</TableCell>
                            <TableCell style={cellStyle}>
                                <span style={codeStyle}>{get(driver, ['construct_code'], '')}</span>
                                <span> {get(driver, ['name'], '')}</span>
                            </TableCell>
                            <TableCell style={cellStyle}>
                                <span style={typeChipStyle}>{get(driver, ['driver_type'], DASH)}</span>
                            </TableCell>
                            <TableCell style={numberCellStyle}>
                                {get(driver, ['los_allowed']) === null || get(driver, ['los_allowed']) === undefined
                                    ? DASH
                                    : get(driver, ['los_allowed'])}
                            </TableCell>
                            <TableCell style={cellStyle}>{get(driver, ['department_code']) || DASH}</TableCell>
                        </TableRow>
                    ))}
                    {unmatched.map((driver, index) => (
                        <TableRow key={`unmatched-${index}`} sx={rowSx}>
                            <TableCell style={{...cellStyle, ...linkishStyle}}>{get(driver, ['input'], DASH)}</TableCell>
                            <TableCell style={cellStyle}>
                                <span style={noMatchChipStyle}>NO CONSTRUCT MATCHED</span>
                            </TableCell>
                            <TableCell style={cellStyle}/>
                            <TableCell style={numberCellStyle}/>
                            <TableCell style={cellStyle}/>
                        </TableRow>
                    ))}
                    {matched.length + unmatched.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5} style={{...cellStyle, textAlign: 'center', color: '#8195ad'}}>
                                No driver came off the discharge summary.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Section>
    );
}

function ItemInclusionsSection({construct, named}) {
    const items = get(construct, ['item_inclusions'], []) || [];

    return (
        <Section title="Runtime construct — item inclusions" count={items.length}>
            <Table size="small" style={{minWidth: '860px'}}>
                <TableHead>
                    <TableRow>
                        <TableCell style={headerCellStyle}>Item</TableCell>
                        <TableCell style={headerCellStyle}>Category</TableCell>
                        <TableCell style={headerCellStyle}>Qty type</TableCell>
                        <TableCell style={{...headerCellStyle, textAlign: 'right'}}>Qty</TableCell>
                        <TableCell style={{...headerCellStyle, textAlign: 'right'}}>Allowed total</TableCell>
                        <TableCell style={headerCellStyle}>From constructs</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item, index) => (
                        <TableRow key={`${get(item, ['item_code'], index)}-${index}`} sx={rowSx}>
                            <TableCell style={cellStyle}>
                                <span style={codeStyle}>{get(item, ['item_code'], '')}</span>
                                <span> {get(item, ['item_name'], '')}</span>
                            </TableCell>
                            <TableCell style={{...cellStyle, ...linkishStyle}}>
                                {get(item, ['category_name'], DASH)}
                            </TableCell>
                            <TableCell style={cellStyle}>{get(item, ['qty_type'], DASH)}</TableCell>
                            <TableCell style={numberCellStyle}>{get(item, ['qty'], DASH)}</TableCell>
                            <TableCell style={strongNumberStyle}>{get(item, ['allowed_total'], DASH)}</TableCell>
                            <TableCell style={cellStyle}>
                                <SourceChips sources={get(item, ['sources'], [])} named={named}/>
                            </TableCell>
                        </TableRow>
                    ))}
                    {items.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={6} style={{...cellStyle, textAlign: 'center', color: '#8195ad'}}>
                                No item is included by name.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Section>
    );
}

function CategoryInclusionsSection({construct, named}) {
    const categories = get(construct, ['category_inclusions'], []) || [];

    return (
        <Section title="Runtime construct — category inclusions" count={categories.length}>
            <Table size="small" style={{minWidth: '780px'}}>
                <TableHead>
                    <TableRow>
                        <TableCell style={headerCellStyle}>Category</TableCell>
                        <TableCell style={headerCellStyle}>Qty type</TableCell>
                        <TableCell style={{...headerCellStyle, textAlign: 'right'}}>Qty</TableCell>
                        <TableCell style={{...headerCellStyle, textAlign: 'right'}}>Allowed total</TableCell>
                        <TableCell style={headerCellStyle}>From constructs</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {categories.map((category, index) => (
                        <TableRow key={`${get(category, ['category_code'], index)}-${index}`} sx={rowSx}>
                            <TableCell style={cellStyle}>
                                <span style={codeStyle}>{get(category, ['category_code'], '')}</span>
                                <span> {get(category, ['category_name'], '')}</span>
                            </TableCell>
                            <TableCell style={cellStyle}>{get(category, ['qty_type'], DASH)}</TableCell>
                            <TableCell style={numberCellStyle}>{get(category, ['qty'], DASH)}</TableCell>
                            <TableCell style={strongNumberStyle}>{get(category, ['allowed_total'], DASH)}</TableCell>
                            <TableCell style={cellStyle}>
                                <SourceChips sources={get(category, ['sources'], [])} named={named}/>
                            </TableCell>
                        </TableRow>
                    ))}
                    {categories.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5} style={{...cellStyle, textAlign: 'center', color: '#8195ad'}}>
                                No category is included on its own.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Section>
    );
}

function ClustersSection({construct}) {
    const clusters = get(construct, ['clusters'], []) || [];

    return (
        <Section title="Line-item clusters & applied limits" count={clusters.length}>
            <Table size="small" style={{minWidth: '860px'}}>
                <TableHead>
                    <TableRow>
                        <TableCell style={{...headerCellStyle, textAlign: 'right'}}>#</TableCell>
                        <TableCell style={headerCellStyle}>Cluster (similar bill lines)</TableCell>
                        <TableCell style={{...headerCellStyle, textAlign: 'right'}}>Lines</TableCell>
                        <TableCell style={{...headerCellStyle, textAlign: 'right'}}>Charged qty</TableCell>
                        <TableCell style={{...headerCellStyle, textAlign: 'right'}}>Allowed qty</TableCell>
                        <TableCell style={headerCellStyle}>Status</TableCell>
                        <TableCell style={headerCellStyle}>Remarks</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* The # is where the row sits in this table, counted from 1 - not the
                        engine's own `cluster_id`, which counts from 0 and is nothing a reader
                        of this table needs. The key still stands on the id. */}
                    {clusters.map((cluster, index) => (
                        <TableRow key={`${get(cluster, ['cluster_id'], index)}-${index}`} sx={rowSx}>
                            <TableCell style={{...numberCellStyle, color: '#8195ad'}}>
                                {index + 1}
                            </TableCell>
                            <TableCell style={{...cellStyle, fontWeight: 600}}>
                                {get(cluster, ['name'], DASH)}
                            </TableCell>
                            <TableCell style={numberCellStyle}>{get(cluster, ['lines'], DASH)}</TableCell>
                            <TableCell style={numberCellStyle}>{get(cluster, ['charged_qty'], DASH)}</TableCell>
                            <TableCell style={strongNumberStyle}>{get(cluster, ['allowed_qty'], DASH)}</TableCell>
                            <TableCell style={cellStyle}>
                                <span style={statusChipStyle(get(cluster, ['status']))}>
                                    {get(cluster, ['status'], DASH)}
                                </span>
                            </TableCell>
                            <TableCell style={{...cellStyle, color: '#63788f'}}>
                                {get(cluster, ['remarks'], '')}
                            </TableCell>
                        </TableRow>
                    ))}
                    {clusters.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={7} style={{...cellStyle, textAlign: 'center', color: '#8195ad'}}>
                                No bill line reached the construct.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Section>
    );
}

function DynamicConstruct({construct, deduction}) {
    // No construct on the response is the ordinary case for a claim the engine has not run,
    // and an empty panel says less than no panel at all.
    if (!construct) {
        return null;
    }

    const named = codeToName(construct);

    return (
        <Box>
            <SummaryCards construct={construct} deduction={deduction}/>
            <DriversSection construct={construct}/>
            <ItemInclusionsSection construct={construct} named={named}/>
            <CategoryInclusionsSection construct={construct} named={named}/>
            <ClustersSection construct={construct}/>
        </Box>
    );
}

export default DynamicConstruct;
